import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { STATUS_OPTIONS } from '../../StatusDropdown/statusOptions';
import { TASK_EDIT_TOOLBAR_STATUS_AS_DROPDOWN } from '../../../../../shared/constants/featureFlags';
import DatePickerCalendar from '../TaskDueDate/DatePickerCalendar/DatePickerCalendar';
import ProgressIndicator from '../ProgressIndicator/ProgressIndicator';
import ToolbarStyleTooltip from '../../../../../ui/components/ToolbarStyleTooltip/ToolbarStyleTooltip';
import { isSequentialTask } from '../../../utils/sequentialTasks';
import { formatDueDate } from '../../../utils/taskUtils';
import { useAuth } from '../../../../../features/auth/contexts/AuthContext';
import { trackEvent } from '../../../../../shared/utils/analytics';
import './TaskEditToolbar.css';

/** Width (px) of the inline toolbar when expanded on desktop; used for collapse/expand animation */
const INLINE_TOOLBAR_EXPANDED_WIDTH_PX = 196;

/** Narrower expanded width on mobile when indent/outdent/add-subtask are in a submenu */
const INLINE_TOOLBAR_EXPANDED_WIDTH_MOBILE_PX = 144;

/** Shorten due date label for toolbar button (matches TaskDueDate shortening) */
const DUE_LABEL_SHORT = {
  Today: 'Td',
  Tomorrow: 'Tm',
  Later: 'Lt',
  Sunday: 'Sun',
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat'
};

const TOOLTIP_COPY = {
  toolbarDisabled: 'Select a task to enable',
  parentTaskStatus: 'Parent task indicates subtask status',
  status: 'Status',
  statusTrigger: {
    label: 'Custom status',
    currentPrefix: 'Current: '
  },
  statusSequentialBlocked: {
    label: 'Custom status',
    subtitle: 'Complete previous tasks first.'
  },
  deleteTask: {
    label: 'Delete task',
    shortcut: '[Backspace] when empty'
  },
  outdent: {
    label: 'Outdent',
    subtitle: 'Move up one level',
    inactive: {
      label: 'Cannot outdent',
      subtitle: 'This task is not a subtask'
    },
    shortcut: 'Shift+Tab'
  },
  indent: {
    label: 'Indent',
    subtitle: <>Make <i>this task</i> a subtask</>,
    inactive: {
      label: 'Cannot indent',
      subtitle: 'No task above to indent under'
    },
    shortcut: 'Tab'
  },
  addSubtask: {
    label: 'Add subtask',
    subtitle: <>Add a subtask to <i>this task</i></>
  },
  sequential: {
    remove: 'Remove sequential task',
    make: 'Create sequential task',
    makeSubtitle: 'Tasks that need to be completed in order.',
    makeShortcut: '1 + .'
  },
  dueDate: {
    label: 'Set due date',
    subtitle: 'Type a date/day in the task to add automatically',
    parent: {
      label: 'Set due date on subtasks',
      subtitle: 'Setting due date on parent task updates due dates for all subtasks.'
    }
  }
};

function formatDateForInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayDate() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return formatDateForInput(today);
}

function getTomorrowDate() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return formatDateForInput(tomorrow);
}

function getLaterDate() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const randomDays = Math.floor(Math.random() * 8) + 7;
  const laterDate = new Date(today);
  laterDate.setDate(today.getDate() + randomDays);
  let dayOfWeek = laterDate.getDay();
  if (dayOfWeek === 0) laterDate.setDate(laterDate.getDate() + 1);
  else if (dayOfWeek === 6) laterDate.setDate(laterDate.getDate() + 2);
  return formatDateForInput(laterDate);
}

function TaskEditToolbar({
  task,
  tasks,
  currentStatus,
  onStatusSelect,
  hasSubtasks = false,
  subtaskStats = null,
  isSequentialDisabled = false,
  canUnindent,
  canIndent,
  onUnindent,
  onIndent,
  onUpdateTaskDueDate,
  onFocusAfterDueDateSet,
  onMakeSequential,
  onRemoveSequential,
  onAddSubtask,
  onRequestDeleteTask,
  filter,
  inline = false,
  isMobile = false,
  inlineCollapsed = false,
  inlineCollapsedInstant = false,
  inlineExpandedByUser = false,
  onInlineExpandClick,
  topToolbar = false,
  showLabels = false,
  disabled = false
}) {
  const { isLoggedIn } = useAuth();
  const isDisabled = !!disabled;
  const taskSubjectLower = topToolbar ? 'the selected task' : 'this task';
  const taskSubjectCapitalized = topToolbar ? 'The selected task' : 'This task';
  const indentTooltipSubtitle = <>Make <i>{taskSubjectLower}</i> a subtask</>;
  const addSubtaskTooltipSubtitle = <>Add a subtask to <i>{taskSubjectLower}</i></>;
  const outdentInactiveSubtitle = `${taskSubjectCapitalized} is not a subtask`;

  const trackToolbarClick = (suffix) => {
    const eventName = `toolbar_${suffix}`;
    if (isLoggedIn) {
      trackEvent(eventName);
    } else {
      trackEvent(`logged_out_interaction_${eventName}`);
    }
  };
  const isCurrentSequential = isSequentialTask(task);
  const isPlaceholder = task.id === 'placeholder' || task._reactKey === 'placeholder_client_key';
  const [dueDateMenuOpen, setDueDateMenuOpen] = useState(false);
  const [dueDateMenuPosition, setDueDateMenuPosition] = useState({ top: 0, right: 0, bottom: undefined });
  const [dueDateOpenAbove, setDueDateOpenAbove] = useState(false);
  const dueDateButtonRef = useRef(null);
  const dueDateMenuRef = useRef(null);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState({ top: 0, left: 0, right: undefined });
  const statusTriggerRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const [deleteMenuOpen, setDeleteMenuOpen] = useState(false);
  const [deleteMenuPosition, setDeleteMenuPosition] = useState({ top: 0, right: 0, left: undefined });
  const deleteButtonRef = useRef(null);
  const deleteMenuRef = useRef(null);

  const [indentSubmenuOpen, setIndentSubmenuOpen] = useState(false);
  const [indentSubmenuPosition, setIndentSubmenuPosition] = useState({ top: 0, right: 0, left: undefined });
  const indentSubmenuButtonRef = useRef(null);
  const indentSubmenuRef = useRef(null);

  const tooltipAnchorRef = useRef(null);
  const tooltipHideTimeoutRef = useRef(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipShortcut, setTooltipShortcut] = useState('');
  const [tooltipSubtitle, setTooltipSubtitle] = useState('');
  const [tooltipInactive, setTooltipInactive] = useState(false);

  const showTooltip = (e, text, shortcut = '', subtitle = '', inactive = false) => {
    if (tooltipHideTimeoutRef.current) {
      clearTimeout(tooltipHideTimeoutRef.current);
      tooltipHideTimeoutRef.current = null;
    }
    tooltipAnchorRef.current = e.currentTarget;
    // When the toolbar is disabled (e.g. in top-toolbar without selection), always show
    // the "select a task" helper and anchor it to the hovered control.
    if (isDisabled) {
      setTooltipText(TOOLTIP_COPY.toolbarDisabled);
      setTooltipShortcut('');
      setTooltipSubtitle('');
      setTooltipInactive(true);
    } else {
      setTooltipText(text);
      setTooltipShortcut(shortcut);
      setTooltipSubtitle(subtitle);
      setTooltipInactive(inactive);
    }
    setTooltipVisible(true);
  };
  const hideTooltip = () => {
    if (tooltipHideTimeoutRef.current) return;
    tooltipHideTimeoutRef.current = setTimeout(() => {
      tooltipHideTimeoutRef.current = null;
      setTooltipVisible(false);
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (tooltipHideTimeoutRef.current) {
        clearTimeout(tooltipHideTimeoutRef.current);
      }
    };
  }, []);

  const hideInPrioritize = filter === 'prioritize';

  const handleStatusClick = (status, e) => {
    if (isDisabled || hasSubtasks || isSequentialDisabled) return;
    if (e) e.stopPropagation();
    onStatusSelect(task.id, status, 'toolbar');
    setStatusDropdownOpen(false);
  };

  const STATUS_DROPDOWN_MAX_WIDTH = 280;
  const STATUS_DROPDOWN_VIEWPORT_PAD = 8;

  const handleStatusTriggerClick = (e) => {
    e.stopPropagation();
    if (isDisabled || hasSubtasks || isSequentialDisabled) return;
    const willBeOpen = !statusDropdownOpen;
    if (willBeOpen) {
      trackToolbarClick('status');
      setIndentSubmenuOpen(false);
    }
    setStatusDropdownOpen(willBeOpen);
    if (willBeOpen && statusTriggerRef.current) {
      const rect = statusTriggerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setStatusDropdownPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          left: undefined
        });
      } else {
        const leftClamped = Math.min(
          rect.left,
          window.innerWidth - STATUS_DROPDOWN_MAX_WIDTH - STATUS_DROPDOWN_VIEWPORT_PAD
        );
        const left = Math.max(STATUS_DROPDOWN_VIEWPORT_PAD, leftClamped);
        setStatusDropdownPosition({ top: rect.bottom + 4, left, right: undefined });
      }
    }
  };

  const handleUnindentClick = (e) => {
    e.stopPropagation();
    if (isDisabled) return;
    trackToolbarClick('outdent');
    if (canUnindent && onUnindent) onUnindent(task);
  };

  const handleIndentClick = (e) => {
    e.stopPropagation();
    if (isDisabled) return;
    trackToolbarClick('indent');
    if (canIndent && onIndent) onIndent(task);
  };

  const handleDueDateButtonClick = (e) => {
    e.stopPropagation();
    if (isDisabled) return;
    trackToolbarClick('due_date');
    const willBeOpen = !dueDateMenuOpen;
    setDueDateMenuOpen(willBeOpen);
    if (willBeOpen && dueDateButtonRef.current) {
      const rect = dueDateButtonRef.current.getBoundingClientRect();
      const CALENDAR_MENU_ESTIMATED_HEIGHT = 320;
      const MENU_GAP = 4;
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
      const openBelow = spaceBelow >= CALENDAR_MENU_ESTIMATED_HEIGHT;
      const right = window.innerWidth - rect.right;
      if (openBelow) {
        setDueDateMenuPosition({ top: rect.bottom + MENU_GAP, right, bottom: undefined });
        setDueDateOpenAbove(false);
      } else {
        setDueDateMenuPosition({
          top: undefined,
          right,
          bottom: window.innerHeight - rect.top + MENU_GAP
        });
        setDueDateOpenAbove(true);
      }
    }
  };

  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        statusTriggerRef.current &&
        !statusDropdownRef.current.contains(event.target) &&
        !statusTriggerRef.current.contains(event.target)
      ) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [statusDropdownOpen]);

  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setStatusDropdownOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [statusDropdownOpen]);

  useEffect(() => {
    if (!dueDateMenuOpen) return;
    const handleClickOutside = (event) => {
      if (
        dueDateMenuRef.current &&
        dueDateButtonRef.current &&
        !dueDateMenuRef.current.contains(event.target) &&
        !dueDateButtonRef.current.contains(event.target)
      ) {
        setDueDateMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dueDateMenuOpen]);

  useEffect(() => {
    if (!dueDateMenuOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setDueDateMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [dueDateMenuOpen]);

  const handleDeleteButtonClick = (e) => {
    e.stopPropagation();
    if (isDisabled || isPlaceholder) return;
    const willBeOpen = !deleteMenuOpen;
    setDeleteMenuOpen(willBeOpen);
    if (willBeOpen && deleteButtonRef.current) {
      const rect = deleteButtonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setDeleteMenuPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          left: undefined
        });
      } else {
        setDeleteMenuPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          left: undefined
        });
      }
    }
  };

  const handleDeleteTaskConfirm = (e) => {
    e.stopPropagation();
    if (isDisabled) return;
    setDeleteMenuOpen(false);
    trackToolbarClick('delete_task');
    if (onRequestDeleteTask) onRequestDeleteTask(task, 'toolbar');
  };

  useEffect(() => {
    if (!deleteMenuOpen) return;
    const handleClickOutside = (event) => {
      if (
        deleteMenuRef.current &&
        deleteButtonRef.current &&
        !deleteMenuRef.current.contains(event.target) &&
        !deleteButtonRef.current.contains(event.target)
      ) {
        setDeleteMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [deleteMenuOpen]);

  useEffect(() => {
    if (!deleteMenuOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setDeleteMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [deleteMenuOpen]);

  const handleIndentSubmenuButtonClick = (e) => {
    e.stopPropagation();
    if (isDisabled) return;
    const willBeOpen = !indentSubmenuOpen;
    if (willBeOpen) setStatusDropdownOpen(false);
    setIndentSubmenuOpen(willBeOpen);
    if (willBeOpen && indentSubmenuButtonRef.current) {
      const rect = indentSubmenuButtonRef.current.getBoundingClientRect();
      setIndentSubmenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
        left: undefined
      });
    }
  };

  useEffect(() => {
    if (!indentSubmenuOpen) return;
    const handleClickOutside = (event) => {
      if (
        indentSubmenuRef.current &&
        indentSubmenuButtonRef.current &&
        !indentSubmenuRef.current.contains(event.target) &&
        !indentSubmenuButtonRef.current.contains(event.target)
      ) {
        setIndentSubmenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [indentSubmenuOpen]);

  useEffect(() => {
    if (!indentSubmenuOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIndentSubmenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [indentSubmenuOpen]);

  const closeDueDateMenuAndFocusTask = () => {
    setDueDateMenuOpen(false);
    queueMicrotask(() => onFocusAfterDueDateSet?.());
  };

  const handleSetToday = () => {
    if (isDisabled) return;
    onUpdateTaskDueDate(task.id, getTodayDate(), false, 'toolbar');
    closeDueDateMenuAndFocusTask();
  };

  const handleSetTomorrow = () => {
    if (isDisabled) return;
    onUpdateTaskDueDate(task.id, getTomorrowDate(), false, 'toolbar');
    closeDueDateMenuAndFocusTask();
  };

  const handleSetLater = () => {
    if (isDisabled) return;
    onUpdateTaskDueDate(task.id, getLaterDate(), true, 'toolbar');
    closeDueDateMenuAndFocusTask();
  };

  const handleNoDueDate = () => {
    if (isDisabled) return;
    onUpdateTaskDueDate(task.id, null, false, 'toolbar');
    closeDueDateMenuAndFocusTask();
  };

  const handleDateSelect = (date) => {
    if (isDisabled || !date) return;
    onUpdateTaskDueDate(task.id, formatDateForInput(date), false, 'toolbar');
    closeDueDateMenuAndFocusTask();
  };

  const dueDateMenuContent = dueDateMenuOpen ? (
    <div
      ref={dueDateMenuRef}
      data-task-edit-toolbar-calendar
      className="task-edit-toolbar-due-date-menu"
      style={{
        ...(dueDateMenuPosition.top != null && { top: `${dueDateMenuPosition.top}px` }),
        ...(dueDateMenuPosition.bottom != null && { bottom: `${dueDateMenuPosition.bottom}px` }),
        right: `${dueDateMenuPosition.right}px`,
        position: 'fixed',
        zIndex: 10000
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <DatePickerCalendar
        selectedDate={task.due_date}
        openAbove={dueDateOpenAbove}
        onDateSelect={handleDateSelect}
        onTodayClick={handleSetToday}
        onTomorrowClick={handleSetTomorrow}
        onLaterClick={handleSetLater}
        onNoDueDateClick={handleNoDueDate}
      />
    </div>
  ) : null;

  const currentStatusOption = STATUS_OPTIONS.find(
    (o) => o.value === currentStatus || (currentStatus == null && o.value === 'incomplete')
  ) ?? STATUS_OPTIONS.find((o) => o.value === 'incomplete');

  const statusDropdownContent = statusDropdownOpen ? (
    <div
      ref={statusDropdownRef}
      className="task-edit-toolbar-status-dropdown"
      style={{
        position: 'fixed',
        top: `${statusDropdownPosition.top}px`,
        ...(statusDropdownPosition.right != null
          ? { right: `${statusDropdownPosition.right}px`, left: 'auto' }
          : { left: `${statusDropdownPosition.left}px` }),
        zIndex: 10000
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {STATUS_OPTIONS.map((option) => {
        const isActive =
          currentStatus === option.value ||
          (currentStatus == null && option.value === 'incomplete');
        return (
          <button
            key={option.value}
            type="button"
            className={`task-edit-toolbar-status-dropdown-item ${isActive ? 'task-edit-toolbar-status-dropdown-item-active' : ''}`}
            onClick={(e) => handleStatusClick(option.value, e)}
            onMouseDown={(e) => e.preventDefault()}
            style={{ '--status-color': option.color }}
          >
            <span className="task-edit-toolbar-status-dropdown-icon" style={{ color: option.color }}>
              {option.icon}
            </span>
            <span className="task-edit-toolbar-status-dropdown-label">{option.label}</span>
            {isActive && (
              <span className="task-edit-toolbar-status-dropdown-checkmark">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8 L6 11 L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            )}
          </button>
        );
      })}
    </div>
  ) : null;

  const deleteMenuContent = deleteMenuOpen ? (
    <div
      ref={deleteMenuRef}
      className="task-edit-toolbar-delete-menu"
      style={{
        position: 'fixed',
        top: `${deleteMenuPosition.top}px`,
        ...(deleteMenuPosition.right != null
          ? { right: `${deleteMenuPosition.right}px`, left: 'auto' }
          : { left: `${deleteMenuPosition.left}px` }),
        zIndex: 10000
      }}
      onClick={(e) => e.stopPropagation()}
      role="menu"
    >
      <button
        type="button"
        className="task-edit-toolbar-delete-menu-item"
        role="menuitem"
        onClick={handleDeleteTaskConfirm}
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className="task-edit-toolbar-delete-menu-icon" aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
        </span>
        <span className="task-edit-toolbar-delete-menu-label">Delete task</span>
      </button>
    </div>
  ) : null;

  const indentSubmenuContent = indentSubmenuOpen ? (
    <div
      ref={indentSubmenuRef}
      className="task-edit-toolbar-delete-menu task-edit-toolbar-indent-submenu"
      style={{
        position: 'fixed',
        top: `${indentSubmenuPosition.top}px`,
        ...(indentSubmenuPosition.right != null
          ? { right: `${indentSubmenuPosition.right}px`, left: 'auto' }
          : { left: `${indentSubmenuPosition.left}px` }),
        zIndex: 10000
      }}
      onClick={(e) => e.stopPropagation()}
      role="menu"
    >
      <button
        type="button"
        className="task-edit-toolbar-delete-menu-item"
        role="menuitem"
        onClick={(e) => {
          e.stopPropagation();
          if (isDisabled) return;
          setIndentSubmenuOpen(false);
          trackToolbarClick('add_subtask');
          onAddSubtask?.(task, 'toolbar');
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className="task-edit-toolbar-delete-menu-icon" aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
            <g fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 19.5h-1c-2.828 0-4.243 0-5.121-.879C7.5 17.743 7.5 16.328 7.5 13.5v-2m0-3.5v3.5m0 0H12"/>
              <path d="M14.5 19.5c0-1.178 0-1.768.351-2.134C15.204 17 15.77 17 16.9 17h1.2c1.131 0 1.697 0 2.048.366c.352.366.352.956.352 2.134s0 1.768-.352 2.134c-.35.366-.917.366-2.048.366h-1.2c-1.131 0-1.697 0-2.048-.366c-.352-.366-.352-.956-.352-2.134ZM5.786 2h3.428C11.29 2 11.5 3.11 11.5 5s-.211 3-2.286 3H5.786C3.71 8 3.5 6.89 3.5 5s.211-3 2.286-3Z"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.5 9v5m2.5-2.5h-5"/>
            </g>
          </svg>
        </span>
        <span className="task-edit-toolbar-delete-menu-label">Add subtask</span>
      </button>
      <button
        type="button"
        className="task-edit-toolbar-delete-menu-item"
        role="menuitem"
        disabled={!canUnindent}
        onClick={(e) => {
          e.stopPropagation();
          setIndentSubmenuOpen(false);
          trackToolbarClick('outdent');
          if (canUnindent && onUnindent) onUnindent(task);
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className="task-edit-toolbar-delete-menu-icon" aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16">
            <g fill="currentColor" fillRule="evenodd">
              <path d="M13 8a.5.5 0 0 0-.5-.5H5.707l2.147-2.146a.5.5 0 1 0-.708-.708l-3 3a.5.5 0 0 0 0 .708l3 3a.5.5 0 0 0 .708-.708L5.707 8.5H12.5A.5.5 0 0 0 13 8"/>
              <path d="M3.5 4a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 1 0v-7a.5.5 0 0 0-.5-.5"/>
            </g>
          </svg>
        </span>
        <span className="task-edit-toolbar-delete-menu-label">Outdent</span>
      </button>
      <button
        type="button"
        className="task-edit-toolbar-delete-menu-item"
        role="menuitem"
        disabled={!canIndent}
        onClick={(e) => {
          e.stopPropagation();
          setIndentSubmenuOpen(false);
          trackToolbarClick('indent');
          if (canIndent && onIndent) onIndent(task);
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className="task-edit-toolbar-delete-menu-icon" aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16">
            <g fill="currentColor" fillRule="evenodd">
              <path d="M3 8a.5.5 0 0 1 .5-.5h6.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H3.5A.5.5 0 0 1 3 8"/>
              <path d="M12.5 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5"/>
            </g>
          </svg>
        </span>
        <span className="task-edit-toolbar-delete-menu-label">Indent</span>
      </button>
    </div>
  ) : null;

  if (hideInPrioritize) return null;

  const showCollapsed = inline && inlineCollapsed;
  const showCollapsedWithCalendar = showCollapsed;
  /* Normalize due_date to string (placeholder and API may use YYYY-MM-DD without T) */
  const dueDateStr = task.due_date != null && task.due_date !== ''
    ? (typeof task.due_date === 'string' ? task.due_date : (task.due_date.toISOString ? task.due_date.toISOString().split('T')[0] : String(task.due_date)))
    : null;
  const dueBtnActive = (inline || topToolbar) && !!dueDateStr;
  const dueDateRawLabel = dueBtnActive && dueDateStr
    ? (formatDueDate(dueDateStr, task.is_later_date === true)?.text ?? dueDateStr.split('T')[0])
    : null;
  /* Shorten label (Td, Tm, Fri, etc.) and show only date for "Month Date" */
  let dueDateLabel = dueDateRawLabel;
  if (dueDateRawLabel) {
    if (DUE_LABEL_SHORT[dueDateRawLabel]) {
      dueDateLabel = DUE_LABEL_SHORT[dueDateRawLabel];
    } else {
      const parts = dueDateRawLabel.trim().split(/\s+/);
      if (parts.length === 2 && /^\d{1,2}$/.test(parts[1])) dueDateLabel = parts[1];
    }
  }

  const expandedWidthPx = inline && !showCollapsed
    ? (isMobile ? INLINE_TOOLBAR_EXPANDED_WIDTH_MOBILE_PX : INLINE_TOOLBAR_EXPANDED_WIDTH_PX)
    : null;
  const expandedWidthStyle = expandedWidthPx != null
    ? { '--inline-toolbar-expanded-width': `${expandedWidthPx}px` }
    : undefined;

  return (
    <div
      className={`task-edit-toolbar text-non-selectable${inline ? ' task-edit-toolbar-inline' : ''}${topToolbar ? ' task-edit-toolbar-top' : ''}${showLabels ? ' task-edit-toolbar-with-labels' : ''}${isDisabled ? ' task-edit-toolbar-disabled' : ''}${showCollapsed ? ' task-edit-toolbar-inline-collapsed' : ''}${showCollapsedWithCalendar ? ' task-edit-toolbar-inline-collapsed-with-calendar' : ''}${inlineExpandedByUser ? ' task-edit-toolbar-inline-expanded-by-user' : ''}${showCollapsed && inlineCollapsedInstant ? ' task-edit-toolbar-inline-collapsed-instant' : ''}`}
      style={expandedWidthStyle}
      onMouseLeave={hideTooltip}
    >
      {inline && (
        <button
          type="button"
          className="task-edit-toolbar-inline-collapsed-trigger"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onInlineExpandClick?.();
          }}
          onMouseDown={(e) => e.preventDefault()}
          aria-label="Expand toolbar"
          aria-hidden={!showCollapsed}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" aria-hidden>
            <circle cx="4" cy="8" r="1.2" fill="currentColor"/>
            <circle cx="8" cy="8" r="1.2" fill="currentColor"/>
            <circle cx="12" cy="8" r="1.2" fill="currentColor"/>
          </svg>
        </button>
      )}
      <div className={`task-edit-toolbar-inline-sections${inline ? ' task-edit-toolbar-inline-sections-inline' : ''}${showCollapsed ? ' task-edit-toolbar-inline-sections-collapsed' : ''}`} aria-hidden={showCollapsed}>
      <div className="task-edit-toolbar-section task-edit-toolbar-status">
        {TASK_EDIT_TOOLBAR_STATUS_AS_DROPDOWN ? (
          <>
            <button
              ref={statusTriggerRef}
              type="button"
              className={`task-edit-toolbar-status-trigger${hasSubtasks || isSequentialDisabled ? ' task-edit-toolbar-status-disabled' : ''}`}
              onClick={handleStatusTriggerClick}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={(e) => showTooltip(e, hasSubtasks ? TOOLTIP_COPY.parentTaskStatus : (isSequentialDisabled ? TOOLTIP_COPY.statusSequentialBlocked.label : TOOLTIP_COPY.statusTrigger.label), '', hasSubtasks ? '' : (isSequentialDisabled ? TOOLTIP_COPY.statusSequentialBlocked.subtitle : `${TOOLTIP_COPY.statusTrigger.currentPrefix}${currentStatusOption?.label ?? 'Incomplete'}`), isSequentialDisabled)}
              onMouseLeave={hideTooltip}
              aria-label="Custom status"
              aria-disabled={hasSubtasks || isSequentialDisabled}
              aria-haspopup="listbox"
              aria-expanded={statusDropdownOpen}
            >
              <span className="task-edit-toolbar-status-trigger-icon-slot">
                {hasSubtasks ? (
                  <div
                    className="task-edit-toolbar-status-progress-mini task-edit-toolbar-status-progress-mini-in-trigger"
                    aria-hidden
                  >
                    {subtaskStats && subtaskStats.total > 0 ? (
                      <ProgressIndicator
                        task={task}
                        tasks={tasks ?? []}
                        subtaskStats={subtaskStats}
                        isSequentialDisabled={isSequentialDisabled}
                        isBlockingTaskHighlighted={false}
                        isBlockingTaskNotBlocked={false}
                      />
                    ) : (
                      <span className="task-edit-toolbar-status-progress-mini-placeholder" aria-hidden />
                    )}
                  </div>
                ) : (
                  currentStatusOption?.value === 'incomplete' ? (
                    <span className="task-edit-toolbar-incomplete-status-grid" aria-hidden>
                      {['incomplete', 'complete', 'wip', 'waiting'].map((value) => {
                        const opt = STATUS_OPTIONS.find((o) => o.value === value);
                        if (!opt) return null;
                        return (
                          <span
                            key={value}
                            className="task-edit-toolbar-incomplete-status-grid-cell"
                            style={{ color: opt.color }}
                          >
                            {opt.icon}
                          </span>
                        );
                      })}
                    </span>
                  ) : (
                    <span className="task-edit-toolbar-status-icon" style={{ color: currentStatusOption?.color }}>
                      {currentStatusOption?.icon}
                    </span>
                  )
                )}
              </span>
              {showLabels && <span className="task-edit-toolbar-btn-label">Custom status</span>}
              <span className="task-edit-toolbar-status-trigger-chevron">▾</span>
            </button>
            {!hasSubtasks && createPortal(statusDropdownContent, document.body)}
          </>
        ) : (
          STATUS_OPTIONS.map((option) => {
            const isActive =
              currentStatus === option.value ||
              (currentStatus == null && option.value === 'incomplete');
            const statusDisabled = hasSubtasks || isSequentialDisabled;
            return (
              <button
                key={option.value}
                type="button"
                className={`task-edit-toolbar-status-btn ${isActive ? 'task-edit-toolbar-status-btn-active' : ''} ${statusDisabled ? 'task-edit-toolbar-status-disabled' : ''}`}
                onClick={(e) => handleStatusClick(option.value, e)}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={(e) => showTooltip(e, isSequentialDisabled ? TOOLTIP_COPY.statusSequentialBlocked.label : hasSubtasks ? TOOLTIP_COPY.parentTaskStatus : option.label, '', isSequentialDisabled ? TOOLTIP_COPY.statusSequentialBlocked.subtitle : '', isSequentialDisabled)}
                onMouseLeave={hideTooltip}
                style={{ '--status-color': option.color }}
                aria-label={option.label}
                aria-disabled={statusDisabled}
              >
                <span className="task-edit-toolbar-status-icon" style={{ color: option.color }}>
                  {option.icon}
                </span>
              </button>
            );
          })
        )}
      </div>

      <div className="task-edit-toolbar-section">
        {!isMobile && (
          <button
            type="button"
            className="task-edit-toolbar-icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (isDisabled) return;
            trackToolbarClick('add_subtask');
            onAddSubtask?.(task, 'toolbar');
          }}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => showTooltip(e, TOOLTIP_COPY.addSubtask.label, '', addSubtaskTooltipSubtitle)}
            onMouseLeave={hideTooltip}
            aria-label="Add subtask"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" aria-hidden>
              <g fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 19.5h-1c-2.828 0-4.243 0-5.121-.879C7.5 17.743 7.5 16.328 7.5 13.5v-2m0-3.5v3.5m0 0H12"/>
                <path d="M14.5 19.5c0-1.178 0-1.768.351-2.134C15.204 17 15.77 17 16.9 17h1.2c1.131 0 1.697 0 2.048.366c.352.366.352.956.352 2.134s0 1.768-.352 2.134c-.35.366-.917.366-2.048.366h-1.2c-1.131 0-1.697 0-2.048-.366c-.352-.366-.352-.956-.352-2.134ZM5.786 2h3.428C11.29 2 11.5 3.11 11.5 5s-.211 3-2.286 3H5.786C3.71 8 3.5 6.89 3.5 5s.211-3 2.286-3Z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.5 9v5m2.5-2.5h-5"/>
              </g>
            </svg>
            {showLabels && <span className="task-edit-toolbar-btn-label">Add subtask</span>}
          </button>
        )}
        <button
          type="button"
          className={`task-edit-toolbar-icon-btn ${isCurrentSequential ? 'task-edit-toolbar-sequential-btn-active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isDisabled) return;
            if (isCurrentSequential && onRemoveSequential) {
              trackToolbarClick('remove_sequential');
              onRemoveSequential(task);
            } else if (onMakeSequential) {
              trackToolbarClick('make_sequential');
              onMakeSequential(task);
            }
          }}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={(e) => showTooltip(e, isCurrentSequential ? TOOLTIP_COPY.sequential.remove : TOOLTIP_COPY.sequential.make, isCurrentSequential ? '' : TOOLTIP_COPY.sequential.makeShortcut, isCurrentSequential ? '' : TOOLTIP_COPY.sequential.makeSubtitle)}
          onMouseLeave={hideTooltip}
          aria-label={isCurrentSequential ? 'Remove sequential task' : 'Make sequential tasks'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" aria-hidden>
            <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1">
              <path d="M.5 10.967h13m-2.5 2.5l2.5-2.5l-2.5-2.5m-8.8-2.69V1.511c0 .589-.478 1.066-1.067 1.066H.778m2.844 3.201H.778m7.644-.001H5.578v-.606c0-.423.25-.807.639-.977L7.8 3.502a1.04 1.04 0 0 0-.417-1.991h-.739c-.464 0-.86.296-1.006.71m6.545 1.245c.59 0 1.067.478 1.067 1.067v.178c0 .589-.478 1.067-1.067 1.067h-.71c-.465 0-.86-.297-1.007-.712"/>
              <path d="M10.62 2.222a.98.98 0 0 1 .941-.711h.533a.978.978 0 1 1 0 1.955h-.088"/>
            </g>
          </svg>
          {showLabels && <span className="task-edit-toolbar-btn-label">Sequential</span>}
        </button>
      </div>
      </div>

      <div className={`task-edit-toolbar-section task-edit-toolbar-due${inline ? ' task-edit-toolbar-inline-due' : ''}`}>
          <button
            ref={dueDateButtonRef}
            type="button"
            className={`task-edit-toolbar-due-btn${dueBtnActive ? ' task-edit-toolbar-due-btn-active' : ''}${dueBtnActive ? ' task-edit-toolbar-due-btn-with-label' : ''}`}
            onClick={handleDueDateButtonClick}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => {
              const due = hasSubtasks ? TOOLTIP_COPY.dueDate.parent : TOOLTIP_COPY.dueDate;
              showTooltip(e, due.label, '', due.subtitle);
            }}
            onMouseLeave={hideTooltip}
            aria-label="Due date"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 9v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9M4 9V7a2 2 0 0 1 2-2h2M4 9h16m0 0V7a2 2 0 0 0-2-2h-2m0 0V3m0 2H8m0-2v2"/></svg>
            {dueDateLabel && (
              <div className="task-edit-toolbar-due-btn-label" aria-hidden>
                {dueDateLabel}
              </div>
            )}
            {showLabels && <span className="task-edit-toolbar-btn-label">Due date</span>}
          </button>
        </div>

      <div className="task-edit-toolbar-section task-edit-toolbar-indent">
        {isMobile ? (
          <button
            ref={indentSubmenuButtonRef}
            type="button"
            className="task-edit-toolbar-icon-btn"
            onClick={handleIndentSubmenuButtonClick}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => showTooltip(e, TOOLTIP_COPY.addSubtask.label, '', addSubtaskTooltipSubtitle)}
            onMouseLeave={hideTooltip}
            aria-label="Subtask actions"
            aria-haspopup="menu"
            aria-expanded={indentSubmenuOpen}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" aria-hidden>
              <g fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 19.5h-1c-2.828 0-4.243 0-5.121-.879C7.5 17.743 7.5 16.328 7.5 13.5v-2m0-3.5v3.5m0 0H12"/>
                <path d="M14.5 19.5c0-1.178 0-1.768.351-2.134C15.204 17 15.77 17 16.9 17h1.2c1.131 0 1.697 0 2.048.366c.352.366.352.956.352 2.134s0 1.768-.352 2.134c-.35.366-.917.366-2.048.366h-1.2c-1.131 0-1.697 0-2.048-.366c-.352-.366-.352-.956-.352-2.134ZM5.786 2h3.428C11.29 2 11.5 3.11 11.5 5s-.211 3-2.286 3H5.786C3.71 8 3.5 6.89 3.5 5s.211-3 2.286-3Z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.5 9v5m2.5-2.5h-5"/>
              </g>
            </svg>
          </button>
        ) : (
          <>
            <button
              type="button"
              className={`task-edit-toolbar-indent-btn ${!canUnindent ? 'task-edit-toolbar-indent-btn-disabled' : ''}`}
              onClick={handleUnindentClick}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={(e) => showTooltip(e, canUnindent ? TOOLTIP_COPY.outdent.label : TOOLTIP_COPY.outdent.inactive.label, TOOLTIP_COPY.outdent.shortcut, canUnindent ? TOOLTIP_COPY.outdent.subtitle : outdentInactiveSubtitle, !canUnindent)}
              onMouseLeave={hideTooltip}
              aria-disabled={!canUnindent}
              aria-label="Outdent task"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" aria-hidden>
                <g fill="currentColor" fillRule="evenodd">
                  <path d="M13 8a.5.5 0 0 0-.5-.5H5.707l2.147-2.146a.5.5 0 1 0-.708-.708l-3 3a.5.5 0 0 0 0 .708l3 3a.5.5 0 0 0 .708-.708L5.707 8.5H12.5A.5.5 0 0 0 13 8"/>
                  <path d="M3.5 4a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 1 0v-7a.5.5 0 0 0-.5-.5"/>
                </g>
              </svg>
              {showLabels && <span className="task-edit-toolbar-btn-label">Outdent</span>}
            </button>
            <button
              type="button"
              className={`task-edit-toolbar-indent-btn ${!canIndent ? 'task-edit-toolbar-indent-btn-disabled' : ''}`}
              onClick={handleIndentClick}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={(e) => showTooltip(e, canIndent ? TOOLTIP_COPY.indent.label : TOOLTIP_COPY.indent.inactive.label, TOOLTIP_COPY.indent.shortcut, canIndent ? indentTooltipSubtitle : TOOLTIP_COPY.indent.inactive.subtitle, !canIndent)}
              onMouseLeave={hideTooltip}
              aria-disabled={!canIndent}
              aria-label="Indent as subtask"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" aria-hidden>
                <g fill="currentColor" fillRule="evenodd">
                  <path d="M3 8a.5.5 0 0 1 .5-.5h6.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H3.5A.5.5 0 0 1 3 8"/>
                  <path d="M12.5 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5"/>
                </g>
              </svg>
              {showLabels && <span className="task-edit-toolbar-btn-label">Indent</span>}
            </button>
          </>
        )}
      </div>

      <div className="task-edit-toolbar-section task-edit-toolbar-delete-last">
        <button
          ref={deleteButtonRef}
          type="button"
          className={`task-edit-toolbar-delete-btn${isPlaceholder ? ' task-edit-toolbar-delete-btn-disabled' : ''}`}
          disabled={isPlaceholder}
          onClick={handleDeleteButtonClick}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={(e) => !isPlaceholder && showTooltip(e, TOOLTIP_COPY.deleteTask.label, TOOLTIP_COPY.deleteTask.shortcut)}
          onMouseLeave={hideTooltip}
          aria-label="Delete task"
          aria-haspopup="menu"
          aria-expanded={deleteMenuOpen}
          aria-disabled={isPlaceholder}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
          {showLabels && <span className="task-edit-toolbar-btn-label">Delete</span>}
        </button>
      </div>

      {createPortal(dueDateMenuContent, document.body)}
      {createPortal(deleteMenuContent, document.body)}
      {createPortal(indentSubmenuContent, document.body)}

      <ToolbarStyleTooltip
        anchorRef={tooltipAnchorRef}
        visible={tooltipVisible && !statusDropdownOpen && !dueDateMenuOpen && !deleteMenuOpen && !indentSubmenuOpen}
        text={tooltipText}
        subtitle={tooltipSubtitle}
        shortcut={tooltipShortcut}
        inactive={tooltipInactive}
        placement="top"
        portal
        openDelay={150}
      />
    </div>
  );
}

export default TaskEditToolbar;
